import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Select,
  MenuItem,
  Stack,
  IconButton
} from '@mui/material';
import {
  Button,
  Typography,
  TextField,
  Popover,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import LockIcon from '@mui/icons-material/Lock';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import Title from '../../components/Title';
import { httpRequest } from '../../api/helper';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, TT_BG_COLOR } from '../../common/AppConstants';
import { formatTimeToAmPm } from '../../common/AppUtils';

const TITLE = 'Template';
const SHIFT_LIST_API = 'organization/shift/'
const API_URL = 'time-table/canvas-config/';

const SLOT_TYPES = {
  FREE: 'free',
  BREAK: 'break',
  RESERVED: 'reserved',
};

const SLOT_CONFIG = {
  [SLOT_TYPES.FREE]: {
    label: 'Allot',
    color: '#4CAF50',
    Icon: EventAvailableIcon,
  },
  [SLOT_TYPES.BREAK]: {
    label: 'Break',
    color: '#FF9800', // F44336
    Icon: FreeBreakfastIcon,
  },
  // [SLOT_TYPES.RESERVED]: {
  //   label: 'Block',  // Reserved
  //   color: '#03A9F4', // 03A9F4
  //   Icon: LockIcon,
  // },
};

const getSlotType = (is_reserved, is_break) => {
  if (is_reserved) return SLOT_TYPES.RESERVED;
  if (is_break) return SLOT_TYPES.BREAK;
  return SLOT_TYPES.FREE;
};

const getSlotFlags = (slotType) => {
  return {
    is_reserved: slotType === SLOT_TYPES.RESERVED,
    is_break: slotType === SLOT_TYPES.BREAK,
  };
};

const DAY_MAP = {
  0: 'Monday',
  1: 'Tuesday',
  2: 'Wednesday',
  3: 'Thursday',
  4: 'Friday',
  5: 'Saturday',
  6: 'Sunday',
}

const getCellColor = (cellDict) => {
  if (cellDict.is_reserved)
    return TT_BG_COLOR.RESERVED
  if (cellDict.is_break)
    return TT_BG_COLOR.BREAK
  return TT_BG_COLOR.CELL
};

export default function EditableTimeTable() {
  const { addNotification } = useNotification();
  const containerRef = useRef(null); // Ref for the box container
  const tableRef = useRef(null); // Ref for the table container

  const [shiftList, setShiftList] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [template, setTemplate] = useState({
    period_label_list: [],
    day_list: [],
  });

  const handlePrevShift = () => {
    if (selectedShift > 0) setSelectedShift(selectedShift - 1);
  };

  const handleNextShift = () => {
    if (selectedShift < shiftList.length - 1) setSelectedShift(selectedShift + 1);
  };

  const handleAddRow = () => {
    const timeCount = template.period_label_list.length;
    let newTimeLabel = `Period ${timeCount + 1}`;

    // TimeRangePicker
    const lastPeriodDict = template.period_label_list[template.period_label_list.length - 1];
    const lastTo = lastPeriodDict.end_time;
    const [lastToHour, lastToMinute] = lastTo.split(':').map(Number);
    const nextToTime = new Date();
    nextToTime.setHours(lastToHour, lastToMinute + 45, 0);
    const newFrom = lastTo;
    const newTo = `${String(nextToTime.getHours()).padStart(2, '0')}:${String(nextToTime.getMinutes()).padStart(2, '0')}`;

    let period_label_dict = {
      name: newTimeLabel,
      start_time: newFrom,
      end_time: newTo
    };

    const updatedTemplate = {
      ...template,
      period_label_list: [...template.period_label_list, period_label_dict],
      day_list: template.day_list.map((day) => ({
        ...day,
        period_list: [
          ...day.period_list,
          {
            is_break: day.period_list[day.period_list.length - 1].is_break,
            is_reserved: day.period_list[day.period_list.length - 1].is_reserved
          },
        ],
      })),
    };

    setTemplate(updatedTemplate);
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const handleRemoveRow = () => {
    if (template.period_label_list.length > 1) {
      const updatedTemplate = {
        ...template,
        period_label_list: template.period_label_list.slice(0, -1),
        day_list: template.day_list.map((day) => ({
          ...day,
          period_list: day.period_list.slice(0, -1),
        })),
      };
      setTemplate(updatedTemplate);
    } else {
      addNotification('Please keep at least 1 slot', 'warning');
    }
  };

  const handleAddColumn = () => {
    const dayCount = template.day_list.length;
    if (dayCount === Object.keys(DAY_MAP).length) {
      addNotification('All the days are already added', 'warning');
      return
    }

    const lastDay = template.day_list[dayCount - 1];
    const newDay = {
      day_of_week: dayCount,
      period_list: lastDay.period_list.map((slot) => ({
        is_break: slot.is_break,
        is_reserved: slot.is_reserved
      })),
    };

    setTemplate({
      ...template,
      day_list: [...template.day_list, newDay],
    });

    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollTo({
          left: tableRef.current.scrollWidth,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const handleRemoveColumn = () => {
    if (template.day_list.length > 1) {
      setTemplate({
        ...template,
        day_list: template.day_list.slice(0, -1),
      });
    } else {
      addNotification('Please keep at least 1 day', 'warning');
    }
  };

  const handleEditTimeLabel = (periodIndex, value) => {
    const updatedTimeLabelList = [...template.period_label_list];
    updatedTimeLabelList[periodIndex].name = value;

    setTemplate({
      ...template,
      period_label_list: updatedTimeLabelList,
    });
  };

  const handleEditTimeStart = (periodIndex, value) => {
    const updatedTimeLabelList = [...template.period_label_list];
    updatedTimeLabelList[periodIndex].start_time = value.format('HH:mm'); // Format Day.js object to HH:mm

    setTemplate({
      ...template,
      period_label_list: updatedTimeLabelList,
    });
  };

  const handleEditTimeEnd = (periodIndex, value) => {
    const updatedTimeLabelList = [...template.period_label_list];
    updatedTimeLabelList[periodIndex].end_time = value.format('HH:mm'); // Format Day.js object to HH:mm

    setTemplate({
      ...template,
      period_label_list: updatedTimeLabelList,
    });
  };

  const handleSlotTypeChange = (rowIndex, colIndex, newSlotType) => {
    const updatedDayList = [...template.day_list];
    const flags = getSlotFlags(newSlotType);
    updatedDayList[colIndex].period_list[rowIndex] = {
      ...updatedDayList[colIndex].period_list[rowIndex],
      ...flags,
    };

    setTemplate({
      ...template,
      day_list: updatedDayList,
    });
  };

  const handleUpdate = async () => {
    const payload = {
      shift: shiftList[selectedShift].id,
      template: template,
    };

    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: payload,
      onSuccess: (data) => {
        setTemplate(data.template);
      },
      addNotification,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshShiftList();
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, [selectedShift]);

  const refreshShiftList = async (event) => {
    await httpRequest({
      method: 'GET',
      url: SHIFT_LIST_API,
      onSuccess: (data) => {
        setShiftList(data.rows);
        if (data.rows.length > 0) {
          setSelectedShift(0);
        }
      },
      onFailure: (data) => {
        setShiftList([]);
      },
      addNotification,
    });
  };

  const refreshData = async (event) => {
    if (selectedShift === null) {
      return
    }

    const payload = {
      shift: shiftList[selectedShift].id
    }
    await httpRequest({
      method: 'GET',
      url: API_URL,
      data: payload,
      onSuccess: (data) => {
        setTemplate(data.template);
      },
      onFailure: (data) => {
        setTemplate({ period_label_list: [], day_list: [] });
      },
      addNotification,
    });
  };


  const TimeRangeDisplay = ({ period_dict, onClick }) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 0.25, alignItems: 'center', pl: 0.25 }}>
        {period_dict.start_time && period_dict.end_time ? (
          <>
            <Chip
              label={formatTimeToAmPm(period_dict.start_time)}
              onClick={onClick}
              size="small"
              variant="outlined"
              sx={{
                cursor: 'pointer',
                // backgroundColor: isToday ? 'primary.light' : 'grey.200',
                // color: isToday ? 'primary.contrastText' : 'text.primary',
                height: 16,
                minWidth: 32,
                fontSize: '0.6rem',
              }}
              aria-label={`Start time: ${formatTimeToAmPm(period_dict.start_time)}`}
            />
            {/* <span>-</span> Separator between chips */}
            <Chip
              label={formatTimeToAmPm(period_dict.end_time)}
              onClick={onClick}
              size="small"
              variant="outlined"
              sx={{
                cursor: 'pointer',
                // backgroundColor: isToday ? 'primary.light' : 'grey.200',
                // color: isToday ? 'primary.contrastText' : 'text.primary',
                height: 16,
                minWidth: 32,
                fontSize: '0.6rem',
              }}
              aria-label={`End time: ${formatTimeToAmPm(period_dict.end_time)}`}
            />
          </>
        ) : (
          <Chip
            label="Select Time"
            onClick={onClick}
            size="small"
            variant="outlined"
            sx={{
              cursor: 'pointer',
              backgroundColor: 'grey.200',
              color: 'text.primary',
              height: 16,
              minWidth: 32,
              fontSize: '0.6rem',
            }}
            aria-label="Select Time"
          />
        )}
      </Box>
    );
  }

  const TimeRangePicker = ({ row_index, period_dict }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box>
          {/* Display as simple text */}
          <TimeRangeDisplay period_dict={period_dict} onClick={handleClick} />

          {/* Popover for time pickers */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
              sx: { borderRadius: 4, p: 4 }
            }}
          >
            <Box pb={2} display="flex" flexDirection={"column"} gap={3} alignItems="center">
              <TimePicker
                label="From"
                value={
                  period_dict.start_time && dayjs(period_dict.start_time, 'HH:mm').isValid()
                    ? dayjs(period_dict.start_time, 'HH:mm')
                    : dayjs()
                }
                onChange={(newValue) => handleEditTimeStart(row_index, newValue)}
                ampm={true}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
              {/* <span>-</span> */}
              <TimePicker
                label="To"
                value={
                  period_dict.end_time && dayjs(period_dict.end_time, 'HH:mm').isValid()
                    ? dayjs(period_dict.end_time, 'HH:mm')
                    : dayjs()
                }
                onChange={(newValue) => handleEditTimeEnd(row_index, newValue)}
                ampm={true}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
            </Box>
          </Popover>
        </Box>
      </LocalizationProvider>
    );
  };

  const SlotSelector = ({ slotType, onChange }) => {
    const getIcon = (type) => {
      const config = SLOT_CONFIG[type];
      if (!config) return null;

      const { Icon, color } = config;
      return <Icon sx={{ fontSize: 18, color }} />;
    };

    const getLabel = (type) => {
      return SLOT_CONFIG[type]?.label ?? '';
    };

    return (
      <Select
        variant="standard"  // removes outline/border
        disableUnderline
        value={slotType}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          minWidth: 80,
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          },
        }}
      >
        <MenuItem value={SLOT_TYPES.FREE}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getIcon(SLOT_TYPES.FREE)}
            <Typography variant="body2">{getLabel(SLOT_TYPES.FREE)}</Typography>
          </Box>
        </MenuItem>
        <MenuItem value={SLOT_TYPES.BREAK}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getIcon(SLOT_TYPES.BREAK)}
            <Typography variant="body2">{getLabel(SLOT_TYPES.BREAK)}</Typography>
          </Box>
        </MenuItem>
        <MenuItem value={SLOT_TYPES.RESERVED}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getIcon(SLOT_TYPES.RESERVED)}
            <Typography variant="body2">{getLabel(SLOT_TYPES.RESERVED)}</Typography>
          </Box>
        </MenuItem>
      </Select>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 1,
          }}
        >
          <Box>
            <Title>{TITLE}</Title>
          </Box>

          {selectedShift !==null && shiftList.length > 1 && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <IconButton
                fontSize="small"
                onClick={handlePrevShift} 
                disabled={selectedShift === 0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '50%',
                  color: selectedShift === 0 ? 'text.disabled' : 'primary.main',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <NavigateBeforeOutlinedIcon fontSize="small" />
              </IconButton>

              <Stack
                spacing={0.25}
                alignItems="center"
              >
                <Typography
                  style={{
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    color: '#64748b',
                  }}
                >
                  Shift:
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {shiftList[selectedShift].name}
                </Typography>
              </Stack>

              <IconButton
                fontSize="small"
                onClick={handleNextShift}
                disabled={selectedShift === shiftList.length - 1}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '50%',
                  color: selectedShift === shiftList.length - 1 ? 'text.disabled' : 'primary.main',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <NavigateNextOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}

          <Box>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleUpdate}
              sx={{
                px: 6,
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        ref={containerRef}
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          overflowX: 'auto', // Only the table will scroll
          mt: 2,
        }}
      >
        <TableContainer
          ref={tableRef}
          component={Paper}
          sx={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            overflow: 'show',
          }}
        >
          <Table size="small" sx={{ borderCollapse: 'collapse' }}>
            <TableHead
              style={{
                backgroundColor: TT_BG_COLOR.HEADER,
                color: '#333',
                // borderBottom: '2px solid #ccc',
              }}
            >
              <TableRow
                sx={{
                  display: 'flex',
                }}
              >
                <TableCell
                  key="header_header"
                  align="center"
                  sx={{
                    flex: 1,
                    wordBreak: 'break-word', // Break long words at unnatural point when necessary
                    display: '-webkit-box', // Use webkit box model
                    WebkitBoxOrient: 'vertical', // Set the orientation to vertical for multi-line text
                    WebkitLineClamp: 2, // Limit the text to 2 lines
                    overflow: 'auto', // Add scroll for overflow lines
                    textOverflow: 'ellipsis', // Show ellipsis (...) for overflowing text
                    borderRight: '1px solid #ddd',
                  }}
                >
                  <Typography variant="body2">{template.name}</Typography>
                </TableCell>
                {template.day_list.map((column, col_index) => (
                  <TableCell
                    key={col_index}
                    align="center"
                    sx={{
                      flex: 1,
                      wordBreak: 'break-word', // Break long words at unnatural point when necessary
                      display: '-webkit-box', // Use webkit box model
                      WebkitBoxOrient: 'vertical', // Set the orientation to vertical for multi-line text
                      WebkitLineClamp: 2, // Limit the text to 2 lines
                      overflow: 'auto', // Add scroll for overflow lines
                      textOverflow: 'ellipsis', // Show ellipsis (...) for overflowing text
                      borderRight:
                        col_index !== template.day_list.length - 1
                          ? '1px solid #ddd'
                          : 'none',
                    }}
                  >
                    <Typography variant="body2">{DAY_MAP[column.day_of_week]}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            
            <TableBody>
              {template.period_label_list.map((time_label, time_index) => (
                <TableRow
                  key={time_index}
                  sx={{
                    display: 'flex',
                    '&:hover': {
                      backgroundColor: '#f0f0f0', // Hover effect to highlight row
                    },
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{
                      flex: 1,
                      borderBottom: '1px solid #eee', // Subtle border for table rows
                      wordBreak: 'break-word', // Break long words at unnatural point when necessary
                      display: '-webkit-box', // Use webkit box model
                      WebkitBoxOrient: 'vertical', // Set the orientation to vertical for multi-line text
                      WebkitLineClamp: 2, // Limit the text to 2 lines
                      overflow: 'auto', // Add scroll for overflow lines
                      textOverflow: 'ellipsis', // Show ellipsis (...) for overflowing text
                      borderRight: '1px solid #eee',
                      backgroundColor: TT_BG_COLOR.HEADER,
                    }}
                  >
                    <Box sx={{
                      py: 0.25, px: 0.5,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'space-between',
                      }}
                    >
                      <TextField
                        variant="standard"
                        fontWeight="400"
                        value={time_label.name}
                        onChange={(e) =>
                          handleEditTimeLabel(time_index, e.target.value)
                        }
                        sx={{
                          '& .MuiInput-underline:before': {
                            borderBottom: 'none',
                          }, // Remove underline
                          '& .MuiInputBase-input.Mui-disabled': {
                            borderBottom: 'none', // Remove underline
                            color: 'black', // Ensure disabled text remains the same color
                            WebkitTextFillColor: 'black', // Override browser-specific styles
                            opacity: 1, // Prevents fading
                          },
                          textAlign: 'center',
                          input: { textAlign: 'center' }, // Center text inside input
                        }}
                      />
                      <TimeRangePicker
                        row_index={time_index}
                        period_dict={time_label}
                      />
                    </Box>
                  </TableCell>
                  
                  {template.day_list.map((day, day_index) => {
                    const slot = day['period_list'][time_index];
                    const slotType = getSlotType(slot.is_reserved, slot.is_break);
                    return (
                      <TableCell
                        align="center"
                        sx={{
                          flex: 1,
                          borderBottom: '1px solid #eee', // Subtle border for table rows
                          wordBreak: 'break-word', // Break long words at unnatural point when necessary
                          display: '-webkit-box', // Use webkit box model
                          WebkitBoxOrient: 'vertical', // Set the orientation to vertical for multi-line text
                          WebkitLineClamp: 2, // Limit the text to 2 lines
                          overflow: 'auto', // Add scroll for overflow lines
                          textOverflow: 'ellipsis', // Show ellipsis (...) for overflowing text
                          borderRight:
                            day_index !== template.day_list.length - 1
                              ? '1px solid #eee'
                              : 'none',
                          backgroundColor: getCellColor(day['period_list'][time_index]),
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',      // vertical center
                            justifyContent: 'center',  // horizontal center
                          }}
                        >
                          <SlotSelector
                            slotType={slotType}
                            onChange={(newType) => handleSlotTypeChange(time_index, day_index, newType)}
                          />
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Control Button */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'center', // Centers horizontally
            alignItems: 'center', // Centers vertically (if needed)
            width: '100%', // Ensures full width for horizontal centering
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowCircleLeftIcon />}
            onClick={handleRemoveColumn}
            sx={{
              px: 1,
            }}
          >
            Remove Day
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowCircleUpIcon />}
            onClick={handleRemoveRow}
            sx={{
              px: 1,
            }}
          >
            Remove Period
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowCircleDownIcon />}
            onClick={handleAddRow}
            sx={{
              px: 1,
            }}
          >
            Add Period
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowCircleRightIcon />}
            onClick={handleAddColumn}
            sx={{
              px: 1,
            }}
          >
            Add Day
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
